import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import {
  impersonationStorage,
  isImpersonationValid,
  isPortalPath,
  parseJwt,
  syncSessionFromAccessToken,
} from '@/auth/session';

export { impersonationStorage } from '@/auth/session';
export type { ImpersonationOriginal } from '@/auth/session';

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  request: RetryableRequest;
}

// ── Token-Storage (In-Memory, SRV-B2) ───────────────
//
// Access-Token wird NUR im Arbeitsspeicher gehalten – kein localStorage,
// kein sessionStorage. Bei Page-Reload läuft ein stilles Refresh via
// HttpOnly-Cookie (sr_refresh), das der Browser automatisch mitsendet.
// Dadurch sind Token vor XSS-Zugriffen durch localStorage-Lesen geschützt.

let _accessToken: string | null = null;

export const tokenStorage = {
  getAccess: (): string | null => _accessToken,
  /** @deprecated Refresh-Token ist jetzt als HttpOnly-Cookie gesetzt. */
  getRefresh: (): string | null => null,
  setAccess: (access: string): void => {
    _accessToken = access;
  },
  /**
   * Setzt Access-Token in Memory.
   * Das zweite Argument (refresh) wird ignoriert – der Refresh-Token
   * wird vom Backend als HttpOnly-Cookie gesetzt.
   */
  set: (access: string, _refresh?: string): void => {
    _accessToken = access;
  },
  clear: (): void => {
    _accessToken = null;
  },
};

// ── CSRF-Token (Double-Submit Cookie, SRV-B2) ────────
//
// Das Backend setzt bei Login/Refresh ein lesbares sr_csrf-Cookie.
// Für state-ändernde Requests (POST/PUT/PATCH/DELETE) an /auth/*
// muss der Wert als X-CSRF-Token Header mitgesendet werden.

function _getCsrfToken(): string {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith('sr_csrf='));
  return match ? match.split('=')[1] ?? '' : '';
}

// ── Axios-Instanz ────────────────────────────────────

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
  // Cookies (sr_refresh, sr_csrf) automatisch mitsenden
  withCredentials: true,
});

// ── Request Interceptor: JWT + CSRF beifügen ─────────

const _CSRF_METHODS = new Set(['post', 'put', 'patch', 'delete']);

apiClient.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccess();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // CSRF-Token für state-ändernde Requests auf /auth-Pfade anfügen
    const method = (config.method ?? '').toLowerCase();
    const url = config.url ?? '';
    if (_CSRF_METHODS.has(method) && url.includes('/auth/')) {
      const csrf = _getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// ── Response Interceptor: Token-Refresh bei 401 ─────

let _isRefreshing = false;
let _pendingRequests: PendingRequest[] = [];

function _redirectSuperAdminToAdminAfterRefresh(accessToken: string): boolean {
  const syncResult = syncSessionFromAccessToken(accessToken);
  if (syncResult?.user.role !== 'super_admin') return false;
  if (!isPortalPath(window.location.pathname)) return false;

  impersonationStorage.clear();
  window.location.replace('/admin');
  return true;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const status = error.response?.status;
    const originalRequest = error.config as RetryableRequest | undefined;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _pendingRequests.push({
            resolve,
            reject,
            request: originalRequest,
          });
        });
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        // Cookie-basiertes Refresh: sr_refresh-Cookie wird automatisch mitgesendet.
        // X-CSRF-Token wird im Request-Interceptor gesetzt.
        const resp = await axios.post<{ access_token: string; token_type: string }>(
          '/api/v1/auth/refresh',
          {},
          { withCredentials: true, headers: { 'X-CSRF-Token': _getCsrfToken() } }
        );
        const { access_token } = resp.data;
        tokenStorage.setAccess(access_token);
        const redirectedToAdmin = _redirectSuperAdminToAdminAfterRefresh(access_token);

        if (redirectedToAdmin) {
          _pendingRequests.forEach(({ reject }) => reject(error));
          _pendingRequests = [];
          return Promise.reject(error);
        }

        _pendingRequests.forEach(({ resolve, request }) => {
          if (request.headers) {
            request.headers['Authorization'] = `Bearer ${access_token}`;
          }
          resolve(apiClient(request));
        });
        _pendingRequests = [];

        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        }
        return apiClient(originalRequest);
      } catch {
        tokenStorage.clear();
        _pendingRequests.forEach(({ reject }) => reject(error));
        _pendingRequests = [];

        // Aktive gültige Impersonation nicht pauschal verwerfen:
        // Wenn der Impersonation-Token noch gültig ist, Impersonation beenden
        // und Admin-Session aus Original-Token wiederherstellen (sofern noch gültig).
        const _orig = impersonationStorage.peek();
        impersonationStorage.clear();
        if (_orig && isImpersonationValid(_orig)) {
          const adminPayload = parseJwt(_orig.access);
          if (adminPayload && adminPayload.exp * 1000 > Date.now()) {
            tokenStorage.setAccess(_orig.access);
            window.location.href = '/admin';
            return Promise.reject(error);
          }
        }

        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
